import { Check, ChevronDown, ChevronUp, ClipboardText } from "@ignite-analytics/icons";
import { Button, Card, CardContent, CardHeader, Collapse, Container, List, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { PoweredByFooter } from "../../components/PoweredByFooter";
import { ShapeIcon } from "../../components/ShapeIcon";
import { useAssessments } from "../../hooks/useFormsLists";
import useToken from "../../hooks/useToken";

import { AssessmentListItem } from "./AssessmentListItem";

export const FeedbackOverviewPage: React.FC = () => {
    const [showPreviousReviews, setShowPreviousReviews] = useState(false);

    const { data, isPending: loading } = useAssessments("feedback");
    const { decoded } = useToken();

    const { pendingAssessments, previousAssessments } = useMemo(() => {
        const assessments = data ?? [];
        const pending = assessments
            .filter((a) => a.editable && !a.expired)
            // Nearest deadline first so the most urgent review is at the top;
            // assessments without a deadline sort to the end. filter() already
            // returns a fresh array, so sorting in place is safe.
            .sort((a, b) => {
                if (!a.deadline && !b.deadline) return 0;
                if (!a.deadline) return 1;
                if (!b.deadline) return -1;
                return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
            });
        return {
            pendingAssessments: pending,
            previousAssessments: assessments.filter((a) => !a.editable || a.expired),
        };
    }, [data]);

    const reviewerEmail = decoded.receiverEmail ?? "";
    const tenantName = decoded.customerName ?? "";

    return (
        <Container sx={{ minHeight: "100vh" }} disableGutters={false}>
            <Stack alignItems="center" justifyContent="flex-start" minHeight="100%" gap={4}>
                <Stack justifyContent="center" alignItems="center" py={4} width="100%">
                    <Card
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 4.5,
                        }}
                    >
                        <CardHeader
                            title={
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <ShapeIcon icon={<ClipboardText />} color="accent" size="medium" />
                                    <Stack spacing={0.5} minWidth={0}>
                                        <Typography variant="textXl" fontWeight="bold">
                                            <FormattedMessage defaultMessage="Performance review requests" />
                                        </Typography>
                                        {reviewerEmail && (
                                            <Typography variant="textSm" color="textTextHelper" fontWeight="regular">
                                                {reviewerEmail}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            }
                            subheader={
                                pendingAssessments.length > 0 ? (
                                    <Typography variant="textSm" color="textTextHelper" pt={2}>
                                        <FormattedMessage
                                            defaultMessage="Please share your internal feedback on the suppliers listed below. Your input will contribute to {tenantName}'s overall supplier performance evaluations."
                                            values={{ tenantName }}
                                        />
                                    </Typography>
                                ) : null
                            }
                            sx={{
                                padding: (t) => t.spacing(4, 4, 0, 4),
                            }}
                        />
                        <CardContent
                            sx={{
                                flex: "1 1 auto",
                                overflowY: "auto",
                                padding: (t) => t.spacing(0, 4, 4, 4),
                            }}
                        >
                            {loading ? (
                                <Stack alignItems="center" justifyContent="center" py={6}>
                                    <Typography>
                                        <FormattedMessage defaultMessage="Loading..." />
                                    </Typography>
                                </Stack>
                            ) : pendingAssessments.length === 0 ? (
                                <Stack alignItems="center" justifyContent="center" gap={2} py={6}>
                                    <ShapeIcon icon={<Check />} color="success" size="large" />
                                    <Typography variant="textXl" fontWeight="medium" textAlign="center">
                                        <FormattedMessage defaultMessage="You are all caught up" />
                                    </Typography>
                                    <Typography variant="textMd" color="text.secondary" textAlign="center">
                                        <FormattedMessage defaultMessage="You have completed all your reviews. You will be notified about new requests via email." />
                                    </Typography>
                                </Stack>
                            ) : (
                                <>
                                    <List sx={{ width: "100%" }} disablePadding>
                                        {pendingAssessments.map((assessment) => (
                                            <AssessmentListItem key={assessment.id} assessment={assessment} />
                                        ))}
                                    </List>
                                    <Typography
                                        variant="textSm"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 2 }}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Your feedback is visible to {tenantName} in Ignite and helps drive better supplier collaboration."
                                            values={{ tenantName }}
                                        />
                                    </Typography>
                                </>
                            )}

                            {previousAssessments.length > 0 && (
                                <Stack pt={3} alignItems="flex-start">
                                    <Button
                                        color="ghostGray"
                                        onClick={() => setShowPreviousReviews(!showPreviousReviews)}
                                        endIcon={showPreviousReviews ? <ChevronUp /> : <ChevronDown />}
                                        sx={{ mb: 1 }}
                                    >
                                        <FormattedMessage defaultMessage="Previous reviews" />
                                    </Button>

                                    <Collapse in={showPreviousReviews} sx={{ width: "100%" }}>
                                        <List sx={{ width: "100%" }} disablePadding>
                                            {previousAssessments.map((assessment) => (
                                                <AssessmentListItem key={assessment.id} assessment={assessment} />
                                            ))}
                                        </List>
                                    </Collapse>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
                <Stack mb={12}>
                    <PoweredByFooter />
                </Stack>
            </Stack>
        </Container>
    );
};
